/**
 * reportChecklist.ts — Canonical fire-door inspection checklist metadata.
 *
 * Ported from the web app's `lib/reportChecklist.js` (see
 * /app/memory/web-app-snippets/PHASE_A_B_REPORT_ENHANCEMENTS_PORTING.md
 * section §1). This is the SINGLE SOURCE OF TRUTH for question labels,
 * ordering, risk classification, and defect bucketing across BOTH the
 * inspection form and the PDF renderer. Keep in lock-step with the web
 * app — any change here must ping the web team so both sides mirror.
 *
 * Do NOT duplicate any of the maps below in components or services.
 * Import from this module instead so classifications never drift.
 */

// ── Types ───────────────────────────────────────────────────────────
export type RiskLevel = 'Critical' | 'High' | 'Medium' | 'Low';
// Iter-110ak — Added `'advisory'` for Low-risk fails (missing cert
// label, missing signage, spy viewer, letterbox present, etc.) so
// the PDF renders them in blue "ADVISORY" rather than red "FAIL"
// in the Component Inspection Results grid. Consistent with the
// per-door failure block below the grid.
export type ComponentStatus = 'pass' | 'fail' | 'advisory' | 'na';
export type EffectiveStatus =
  | 'pass'
  | 'pass_with_observations'
  | 'fail'
  | 'review'
  | 'no_access'
  | 'not_inspected'
  | 'draft';

export interface NormalisedComponent {
  key: string;
  label: string;
  status: ComponentStatus;
}

export interface FailedComponentPhoto {
  key?: string;
  question?: string;
  notes?: string;
  photo?: string;
  photos?: any[];
  severity?: string;
  advisory?: boolean;
  risk?: RiskLevel | string;
}

// ── Canonical labels & ordering ─────────────────────────────────────
export const QUESTION_LABELS: Record<string, string> = {
  door_operates_correctly:      'Door leaf',
  door_frame_secure:            'Frame',
  door_label_present:           'Identification label',
  smoke_fire_seals_intact:      'Intumescent / smoke seals',
  smoke_seal_test_pass:         'Cold-smoke seal contact',
  glass_intact:                 'Glazing',
  window_beads_secure:          'Glazing beads',
  handle_lock_secure:           'Handle & latch',
  door_closer_working:          'Self-closer',
  three_hinges_fitted:          'Hinges',
  door_to_stop_bead:            'Perimeter gaps',
  signage_in_place:             'Signage',
  push_plates_secure:           'Push / kick plates',
  dropdown_seal:                'Threshold / dropdown seal',
  spy_viewer:                   'Spy viewer',
  letterbox_present:            'Letterbox present',
  letterbox_fire_rated:         'Letterbox fire-rated',
  letterbox_operates_correctly: 'Letterbox operates correctly',
  hold_open_release_working:    'Hold-open release mechanism',
};

export const QUESTION_ORDER: string[] = [
  'door_operates_correctly', 'door_frame_secure', 'three_hinges_fitted',
  'door_closer_working', 'handle_lock_secure', 'smoke_fire_seals_intact',
  'smoke_seal_test_pass', 'door_to_stop_bead', 'dropdown_seal',
  'glass_intact', 'window_beads_secure', 'signage_in_place',
  'door_label_present', 'push_plates_secure', 'spy_viewer',
  'letterbox_present', 'letterbox_fire_rated', 'letterbox_operates_correctly',
  'hold_open_release_working',
];

// ── Risk classification ─────────────────────────────────────────────
// Risk level when the item FAILS. Drives:
//   • the risk badge shown next to a failed answer in the inspection form
//   • the auto-promotion logic (all-Low-risk fails → PASS WITH OBSERVATIONS)
//   • the Remedial Action Schedule ordering & timescale
export const DEFECT_RISK: Record<string, RiskLevel> = {
  door_operates_correctly:      'Critical',
  door_frame_secure:            'High',
  three_hinges_fitted:          'High',
  door_closer_working:          'Critical',
  hold_open_release_working:    'High',
  smoke_fire_seals_intact:      'High',
  smoke_seal_test_pass:         'High',
  door_to_stop_bead:            'High',
  handle_lock_secure:           'Medium',
  dropdown_seal:                'Medium',
  glass_intact:                 'High',
  window_beads_secure:          'Medium',
  signage_in_place:             'Low',
  door_label_present:           'Low',
  push_plates_secure:           'Medium',
  // Iter-226 — Elevated from 'Low' so a spy-viewer defect the inspector
  // logged an explicit failure card for renders in the Main Failed section
  // (with its photo), not the blue "advisory" bucket where the photo was
  // stripped. A bare "No" with no failure card still reads as "not present"
  // (see OPTIONAL_NOT_PRESENT + the explicit-failure guard in normaliseComponents).
  spy_viewer:                   'Medium',
  letterbox_present:            'Low',
  letterbox_fire_rated:         'High',
  letterbox_operates_correctly: 'Medium',
  gap_measurements:             'High',
};

// ── Executive-summary defect bucketing (9 categories) ───────────────
export const DEFECT_CATEGORY: Record<string, string> = {
  door_label_present:           'missing_labels',
  smoke_fire_seals_intact:      'damaged_seals',
  smoke_seal_test_pass:         'damaged_seals',
  door_closer_working:          'damaged_closers',
  hold_open_release_working:    'damaged_closers',
  door_to_stop_bead:            'excessive_gaps',
  dropdown_seal:                'excessive_gaps',
  signage_in_place:             'missing_signage',
  door_frame_secure:            'damaged_frames',
  door_operates_correctly:      'damaged_leaves',
  glass_intact:                 'glazing_defects',
  window_beads_secure:          'glazing_defects',
  gap_measurements:             'excessive_gaps',
};

export const DEFECT_CATEGORY_LABELS: Record<string, string> = {
  missing_labels:  'Missing Certification Labels',
  damaged_seals:   'Damaged Seals',
  damaged_closers: 'Damaged Closers',
  excessive_gaps:  'Excessive Gaps',
  missing_signage: 'Missing Signage',
  damaged_frames:  'Damaged Frames',
  damaged_leaves:  'Damaged Door Leaves',
  glazing_defects: 'Glazing Defects',
  other_defects:   'Other Defects',
};

// Ordered list used to render the executive-summary defect table.
export const DEFECT_CATEGORY_ORDER: string[] = [
  'missing_labels',
  'damaged_seals',
  'damaged_closers',
  'excessive_gaps',
  'missing_signage',
  'damaged_frames',
  'damaged_leaves',
  'glazing_defects',
  'other_defects',
];

// ── Copy blocks ─────────────────────────────────────────────────────
export const FREQUENCY_JUSTIFICATION: Record<string, string> = {
  '3_month':
    "Communal fire door inspection regime, in accordance with the Responsible Person\u2019s maintenance strategy and Regulation 10 of the Fire Safety (England) Regulations 2022 where applicable.",
  '6_month':
    "Communal fire door inspection regime, in accordance with the Responsible Person\u2019s maintenance strategy and Regulation 10 of the Fire Safety (England) Regulations 2022 where applicable.",
  '12_month':
    "Flat entrance door (FD30S) annual inspection regime under Regulation 10 of the Fire Safety (England) Regulations 2022 for buildings with storeys above 11 metres.",
};

// Remedial Action Schedule — remediation timescale per risk level.
export const RISK_TIMESCALE: Record<RiskLevel, string> = {
  Critical: 'Immediate — within 24 hours',
  High:     'Within 7 days',
  Medium:   'Within 30 days',
  Low:      'Next planned maintenance cycle',
};

// Iter-163m — Realistic lead-time timescales for remedial works that
// physically cannot be completed within the risk-based defaults above.
// Certified fire doorsets, fire-rated frames and glazing are all
// manufactured-to-order items with typical lead times of 2 to 8 weeks
// (doorset/frame) or 2 to 4 weeks (glazing). Property managers were
// getting reports demanding "Immediate — within 24 hours" for a full
// doorset swap, which is neither possible nor a fair compliance ask.
//
// The wording pairs "Interim mitigation immediately" with the realistic
// procurement window so the responsible person still has a clear duty
// to protect occupants (e.g. temporary signage, fire warden checks,
// keeping the compartment clear) while awaiting the manufactured item.
export const REPLACEMENT_TIMESCALE: Record<string, string> = {
  // Whole certified doorset (leaf + frame + certified ironmongery).
  fullDoorset:
    'Interim mitigation immediately; replace within 2 to 8 weeks (manufactured to order)',
  // Fire-resisting frame only (rare in practice — usually paired with a
  // full doorset swap).
  fireRatedFrame:
    'Interim mitigation immediately; replace within 2 to 8 weeks (manufactured to order)',
  // Fire-resisting glazing / vision panel — typically 2 to 4 weeks.
  fireRatedGlass:
    'Interim mitigation immediately; replace within 2 to 4 weeks (manufactured to order)',
  // Fire-rated letterbox — usually available from stock but allow
  // procurement time for certified units.
  letterbox:
    'Within 1 to 2 weeks (fire-rated item procurement)',
};

/**
 * Returns the appropriate remediation timescale for a defect. Falls
 * back to RISK_TIMESCALE by risk level unless the defect kind is a
 * manufacture-to-order replacement (full doorset, fire-rated glazing,
 * etc.) in which case the realistic lead-time wording is used instead.
 */
export function remediationTimescaleFor(opts: {
  key?: string;
  risk: RiskLevel;
  isFullDoorsetReplacement?: boolean;
  needsFireRatedGlazing?: boolean;
}): string {
  if (opts.isFullDoorsetReplacement) return REPLACEMENT_TIMESCALE.fullDoorset;
  if (opts.needsFireRatedGlazing) return REPLACEMENT_TIMESCALE.fireRatedGlass;
  if (opts.key === 'glass_intact') return REPLACEMENT_TIMESCALE.fireRatedGlass;
  if (opts.key === 'letterbox_fire_rated') return REPLACEMENT_TIMESCALE.letterbox;
  return RISK_TIMESCALE[opts.risk] || RISK_TIMESCALE.Medium;
}

// Recommended-action copy for specific low-risk / advisory defects.
// For any key not listed here, the generic fallback is used (see
// `recommendedActionFor()` below).
export const RECOMMENDED_ACTION: Record<string, string> = {
  door_label_present:
    'Record and monitor. The absence of a visible certification label does not automatically indicate non-compliance.',
  signage_in_place:
    'Install / re-fit fire door signage in accordance with BS 5499 and the building fire strategy.',
  spy_viewer:
    'Record and monitor. Retrofit fire-rated spy viewer at next planned maintenance if occupant requests one.',
  letterbox_present:
    'Record and monitor. No action required unless a letterbox is being added.',
  letterbox_fire_rated:
    "Replace with a fire-rated letterbox that meets the door\u2019s rating.",
  letterbox_operates_correctly:
    'Adjust or replace letterbox flap and spring so it closes fully unaided.',
};

export function recommendedActionFor(key: string): string {
  if (RECOMMENDED_ACTION[key]) return RECOMMENDED_ACTION[key];
  const label = (QUESTION_LABELS[key] || key).toLowerCase();
  return `Investigate and remediate: ${label}.`;
}

// Iter-110ak — Canonical "Fail (if ✗)" text per checklist key.
// Rendered in TWO places on the PDF:
//   1. The Inspection Checklist table (top of each door page) — in
//      the "Fail (if ✗)" column when the item is a hard fail.
//   2. The "Failed Components — Reasons" block (further down each
//      door page) — as a fallback chip beneath the question when
//      the inspector didn't tick any specific reason. Prevents the
//      red cards from showing just a question with nothing beneath.
//
// If the inspector DID tick reasons, those override this fallback so
// the report shows their specific observations.
export const FAIL_TEXT_BY_KEY: Record<string, string> = {
  door_label_present:        'Fire door certification could not be verified at the time of inspection.',
  door_frame_secure:         'Frame loose, damaged, or not adequately fixed',
  door_operates_correctly:   'Door damaged or does not operate correctly',
  smoke_fire_seals_intact:   'Seals missing, damaged or poorly fitted',
  smoke_seal_test_pass:      'Smoke seals not effective where required',
  glass_intact:              'Glazing damaged or missing',
  window_beads_secure:       'Beads missing or insecure',
  handle_lock_secure:        'Ironmongery fitted not confirmed as fire-rated',
  door_closer_working:       'Closer not closing door fully or latching',
  three_hinges_fitted:       'Number/type of hinges is not compliant with fire door requirements',
  door_to_stop_bead:         'Stop bead missing or damaged',
  signage_in_place:          'Signage missing or incorrect',
  push_plates_secure:        'Plates loose or damaged',
  finger_protection:         'Finger protection missing or damaged',
  dropdown_seal:             'Drop-down seal not functioning',
  spy_viewer:                'Spy viewer not fire-rated',
  letterbox_present:         'Letterbox not present',
  letterbox_fire_rated:      'Letterbox not fire-rated',
  letterbox_operates_correctly: 'Letterbox flap does not close correctly',
};

export function failTextFor(key: string): string {
  if (FAIL_TEXT_BY_KEY[key]) return FAIL_TEXT_BY_KEY[key];
  const label = QUESTION_LABELS[key] || key;
  return `Fails: ${label.toLowerCase()}`;
}

// Iter-110ak — CANONICAL, INVARIANT wording for the certification-
// label advisory. Rendered identically on every door, every report,
// every time — no per-door variations allowed. Includes HTML `<p>`
// tags so the three paragraphs typeset correctly in the PDF.
//
// Previously the mini-card also appended `fc.notes` (which could
// contain the "solid 44 mm notional fire door" reason) UNDER this
// block, producing inconsistent wording between doors (some had one
// paragraph, some had two). The pdfService now suppresses `fc.notes`
// for cert-label items so this three-paragraph text is the ONLY
// thing rendered.
export const CERTIFICATION_LABEL_ADVISORY_COPY =
  '<p style="margin:0 0 6px 0;">No certification label or plug was visible at the time of inspection. ' +
  'The absence of a visible certification mark does not, in itself, indicate that ' +
  'the fire door is unsuitable or non-compliant. Many existing fire doors may remain ' +
  'acceptable where they continue to provide adequate fire resistance and are maintained ' +
  'in good condition.</p>' +
  '<p style="margin:0 0 6px 0;">Certification could not be verified during the inspection. However, the door is of solid ' +
  'construction and measures approximately 44 mm in thickness, which under previous guidance ' +
  'may have been considered a nominal (notional) fire door. Subject to its overall condition ' +
  'and performance, remedial works or upgrades rather than full replacement may be sufficient.</p>' +
  '<p style="margin:0;">If formal certification is required, further investigation and assessment should be undertaken ' +
  'to determine the door&rsquo;s fire-resisting capabilities and compliance with current standards. ' +
  'Specialist consultation is recommended where certification confirmation is necessary.</p>';

// Iter-163f — Canonical replacement statement for the PDF report when
// the inspector has ticked "This door needs replacing" on the cert-
// label question. The whole doorset is deemed non-compliant, so
// listing individual component failures underneath is noise — the
// report should carry a single, unambiguous overriding statement.
// This flag lives on `checklist_data.door_label_needs_replacing`
// and also on `failed_component_photos[i].needs_replacing === true`
// (for the door_label_present key) — both are checked by the PDF
// renderer so legacy inspections without the flag are handled too.
export const DOORSET_REPLACEMENT_STATEMENT =
  'This doorset has been identified as non-compliant and is not a certificated fire-resisting doorset. ' +
  'To maintain the required compartmentation and fire protection standards, the door, frame and all ' +
  'associated ironmongery/furniture must be replaced with a third-party certificated fire doorset of ' +
  'the appropriate fire-resistance rating (e.g. FD30/FD60), installed in accordance with the ' +
  'manufacturer&rsquo;s instructions and relevant British Standards.';

/**
 * Returns true when the inspection carries the "doorset needs full
 * replacement" flag — either via checklist_data.door_label_needs_replacing
 * (new-code inspections) or a needs_replacing=true fcp on the cert
 * label question (belt-and-braces for legacy rows).
 */
export function doorsetNeedsReplacement(inspection: any): boolean {
  if (!inspection) return false;
  const cd = inspection.checklist_data || {};
  if (cd.door_label_needs_replacing === true) return true;
  const fcps = inspection.failed_component_photos || [];
  return fcps.some(
    (fc: any) => fc?.key === 'door_label_present' && fc?.needs_replacing === true,
  );
}

// ── Optional components ────────────────────────────────────────────
// When these answer FALSE, the interpretation is "no such component
// installed" — NOT "component broken". They never count as a defect
// on their own.
export const OPTIONAL_NOT_PRESENT = new Set<string>([
  'letterbox_present',
  'spy_viewer',
]);

// Iter-226 — True when a failed_component_photos entry carries real
// evidence: a photo, inspector notes, or an explicit hard-fail signal.
export function isEvidenceBackedFcp(fc: any): boolean {
  if (!fc) return false;
  if (fc.needs_replacing === true || fc.severity === 'critical') return true;
  const hasPhoto = !!fc.photo || (Array.isArray(fc.photos) && fc.photos.length > 0);
  const hasNotes = !!(fc.notes && String(fc.notes).trim());
  return hasPhoto || hasNotes;
}

// Iter-226 — Optional components (spy viewer / letterbox present) get a
// failed_component_photos entry whenever the inspector answers "No", but a
// bare "No" with NO evidence means "not installed" — NOT a defect. Such an
// entry is suppressed everywhere (component grid, failed-components list and
// door status) so it never renders as an empty failed card or flips an
// otherwise-passing door to FAIL. WITH evidence (photo/notes) it is a
// genuine failure and flows through normally.
export function isSuppressedOptionalFcp(fc: any): boolean {
  if (!fc || !OPTIONAL_NOT_PRESENT.has(String(fc.key || ''))) return false;
  if (fc.advisory === true) return false;
  return !isEvidenceBackedFcp(fc);
}

// Iter-227 — An OPTIONAL component (spy viewer / letterbox present) that the
// inspector logged an explicit, EVIDENCE-BACKED failure card for (a photo or a
// ticked/typed reason) is a GENUINE hard failure. It must render red in the
// Main Failed section with its photo — NOT the blue advisory bucket where the
// photo is stripped. This override wins over any stale advisory/observation/
// Low flags stamped by an older build (when spy_viewer's DEFECT_RISK was still
// 'Low'), so already-saved inspections render correctly without re-inspecting.
// A bare "No" with no evidence still reads as "not installed" (→ N/A) via
// isSuppressedOptionalFcp above.
export function isEvidenceBackedOptionalFail(fc: any): boolean {
  if (!fc || !OPTIONAL_NOT_PRESENT.has(String(fc.key || ''))) return false;
  return isEvidenceBackedFcp(fc);
}

// Keys that ALWAYS trigger a hard FAIL (never eligible for
// pass-with-observations auto-promotion, never manually overridable
// away from FAIL from within the inspection form).
export const CRITICAL_KEYS: string[] = Object.entries(DEFECT_RISK)
  .filter(([, level]) => level === 'Critical')
  .map(([key]) => key);

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Normalise any truthy/falsy answer format used in the mobile form
 * (boolean, "Yes"/"No", "pass"/"fail", "n/a", null, undefined, "")
 * into one of pass | fail | na.
 */
export function statusFor(v: unknown): ComponentStatus {
  if (v == null || v === '' || v === 'n/a' || v === 'N/A' || v === 'na') return 'na';
  if (v === true || v === 'Yes' || v === 'yes' || v === 'pass' || v === 'Pass' || v === 'PASS') return 'pass';
  if (v === false || v === 'No' || v === 'no' || v === 'fail' || v === 'Fail' || v === 'FAIL') return 'fail';
  return 'na';
}

/**
 * Given a raw inspection object, return the ordered list of all 19
 * components with their pass/fail/na status. Also folds gap
 * measurements > 4 mm into a synthetic "Perimeter gaps" fail so
 * downstream consumers don't have to inspect `gap_measurements` too.
 */
export function normaliseComponents(inspection: any): NormalisedComponent[] {
  const cd = inspection?.checklist_data || {};
  const raw = cd.custom_items || {};

  const rawLetterbox = raw.letterbox_present;
  const noLetterbox =
    rawLetterbox === false ||
    rawLetterbox === 'No' ||
    rawLetterbox === 'no' ||
    (rawLetterbox == null);

  // Iter-226 — Keys the inspector logged an EXPLICIT failure card for
  // (photo / notes / needs-replacing). An "optional" component such as the
  // spy viewer must NOT be silently downgraded to N/A when a real defect
  // with evidence was recorded against it — the inspector expects that
  // failure (and its photo) to surface in the Main Failed section.
  const explicitFailKeys = new Set<string>(
    ((inspection?.failed_component_photos || []) as any[])
      .filter((fc) => {
        if (!fc || !fc.key) return false;
        // Iter-227 — an evidence-backed OPTIONAL component fail (spy viewer /
        // letterbox present, with a photo or a ticked reason) is a genuine
        // fail even if an older build stamped it advisory/observation. Only a
        // bare "No" (no evidence) stays "not installed" → N/A.
        if (OPTIONAL_NOT_PRESENT.has(String(fc.key))) return isEvidenceBackedFcp(fc);
        if (fc.advisory === true || fc.severity === 'observation') return false;
        if (fc.needs_replacing === true || fc.severity === 'critical') return true;
        const hasPhoto = !!fc.photo || (Array.isArray(fc.photos) && fc.photos.length > 0);
        const hasNotes = !!(fc.notes && String(fc.notes).trim());
        return hasPhoto || hasNotes;
      })
      .map((fc) => String(fc.key)),
  );

  const out: NormalisedComponent[] = QUESTION_ORDER.map((key) => {
    let s = statusFor(raw[key]);
    // Optional components: FALSE = not installed, not "broken" — UNLESS the
    // inspector attached an explicit failure card (with evidence) for it.
    if (s === 'fail' && OPTIONAL_NOT_PRESENT.has(key) && !explicitFailKeys.has(key)) s = 'na';
    // If letterbox not present, downstream letterbox checks are n/a.
    if (noLetterbox && (key === 'letterbox_fire_rated' || key === 'letterbox_operates_correctly')) s = 'na';
    // Iter-110ak — Low-risk items (missing cert label, missing signage,
    // spy viewer, letterbox present, push plates, finger protection)
    // that failed the raw check are OBSERVATIONS, not failures. The
    // PDF must render them as blue "ADVISORY" rather than red "FAIL"
    // — consistent with the "1 ADVISORY (NOT A FAILURE)" block that
    // renders below the grid. Otherwise the same fact ends up
    // presented two different ways on one page.
    if (s === 'fail' && (DEFECT_RISK[key] || 'Medium') === 'Low'
        && !(OPTIONAL_NOT_PRESENT.has(key) && explicitFailKeys.has(key))) s = 'advisory';
    // Iter-110ak — Cert-label "door needs replacing" override. When
    // the inspector explicitly ticked that the door isn't suitable
    // for retention, the pill flips back to red FAIL (overriding
    // the Low-risk auto-downgrade). This keeps the grid pill and
    // the failure block in sync — no more mixed messages.
    if (key === 'door_label_present' && s === 'advisory' && cd.door_label_needs_replacing === true) {
      s = 'fail';
    }
    return { key, label: QUESTION_LABELS[key] || key, status: s };
  });

  // Roll gap-measurements > 4 mm into a synthetic Perimeter-gaps fail.
  const gaps = cd.gap_measurements || {};
  for (const v of Object.values(gaps)) {
    const n = parseFloat(String(v ?? ''));
    if (Number.isFinite(n) && n > 4) {
      const row = out.find((r) => r.key === 'door_to_stop_bead');
      if (row) row.status = 'fail';
      break;
    }
  }

  // Iter-228 — An over-tolerance under-door / threshold gap must FAIL the
  // "Threshold / dropdown seal" row (not only "Perimeter gaps"), UNLESS a
  // working drop-down seal bypasses the under-door gap. The bottom-edge gap
  // keys are the threshold measurement; `threshold_gap_within_tolerance:false`
  // is the fallback signal for inspections that stored only the summary flag.
  const THRESHOLD_GAP_KEYS = ['bot_lock', 'bot_mid', 'bot_hinge', 'd2_bot_lock', 'd2_bot_mid', 'd2_bot_hinge'];
  const dropdownSealWorks =
    raw.dropdown_seal === 'Yes' || raw.dropdown_seal === true ||
    cd.threshold_gap_bypassed_by_seal === true;
  if (!dropdownSealWorks) {
    const thresholdGapFail =
      cd.threshold_gap_within_tolerance === false ||
      THRESHOLD_GAP_KEYS.some((k) => {
        const n = parseFloat(String(gaps[k] ?? ''));
        return Number.isFinite(n) && n > 4;
      });
    if (thresholdGapFail) {
      const row = out.find((r) => r.key === 'dropdown_seal');
      if (row && row.status !== 'fail') row.status = 'fail';
    }
  }
  return out;
}

/**
 * True when EVERY failed_component_photos entry is flagged
 * `advisory: true`. Used by `effectiveStatus()` to promote a raw
 * `pass` inspection into `pass_with_observations` when the inspector
 * only recorded advisory-level observations.
 */
export function isAdvisoryOnly(inspection: any): boolean {
  const fcps: FailedComponentPhoto[] = inspection?.failed_component_photos || [];
  return fcps.length > 0 && fcps.every((fc) => fc.advisory === true);
}

/**
 * True when every failed component + every non-advisory failed photo
 * has DEFECT_RISK === 'Low'. Used by `effectiveStatus()` to auto-
 * promote FAIL/REVIEW inspections into `pass_with_observations` when
 * only low-risk observations were recorded.
 */
/**
 * Returns true when the inspection has at least one failure AND every
 * failure (whether from the component grid OR the failed_component_photos
 * array) has DEFECT_RISK === 'Low'. Used by `effectiveStatus()` to auto-
 * promote FAIL/REVIEW inspections into `pass_with_observations` when
 * only low-risk observations were recorded.
 *
 * Iter-110ak — Post-`advisory-status` fix: we now consider components
 * whose normalised status is EITHER `'fail'` OR `'advisory'` as
 * "failures for the purpose of the low-risk check". Otherwise the
 * component-grid downgrade of Low-risk items to `'advisory'`
 * (which was correct for the pill grid) hid them from this counter,
 * causing PWO promotion to silently regress into raw FAIL.
 */
export function allFailuresAreLowRisk(inspection: any): boolean {
  const comps = normaliseComponents(inspection);
  const failedComps = comps.filter((c) => c.status === 'fail' || c.status === 'advisory');
  const fcps: FailedComponentPhoto[] = (inspection?.failed_component_photos || []).filter(
    (fc: FailedComponentPhoto) => !fc.advisory,
  );
  if (failedComps.length === 0 && fcps.length === 0) return false;
  for (const c of failedComps) {
    if ((DEFECT_RISK[c.key] || 'Medium') !== 'Low') return false;
  }
  for (const fc of fcps) {
    const key = fc.key || '';
    if ((DEFECT_RISK[key] || 'Medium') !== 'Low') return false;
  }
  return true;
}

/**
 * THE key function — every consumer of an inspection's status should
 * read this rather than the raw `status` field. This is what lets a
 * door with only a missing label render as PASS WITH OBSERVATIONS
 * even though the backend still stores raw `pass` (workaround until
 * the backend enum adds `pass_with_observations`).
 */
export function effectiveStatus(inspection: any): EffectiveStatus {
  const raw = String(inspection?.status || '').toLowerCase() as EffectiveStatus;
  if (!raw) return 'not_inspected';

  // ── HARD GUARDS (run first, never overridable by breadcrumbs) ────
  //   • `no_access`: the inspector could not access the door — this
  //     is a categorical fact, never demote it to PASS/PWO.
  //   • `review`: explicit inspector escalation.
  //   • `checklist_data.no_access === true`: authoritative even when
  //     the DB row somehow shipped with `status='pass'`.
  const cd = inspection?.checklist_data || {};
  if (cd.no_access === true) return 'no_access';
  if (raw === 'no_access') return 'no_access';
  if (raw === 'review') return 'review';

  // ── FAIL EVIDENCE CHECK — Iter-162 (Old Priory Court fix) ────────
  // Before we honour ANY promotion signal (raw pass, PWO breadcrumb,
  // low-risk auto-promotion), we ask: does the raw data actually
  // support PWO/PASS? A single non-Low-risk failure — either a
  // component-grid `fail` at Medium/High/Critical risk OR a
  // failed_component_photos entry with `advisory !== true` at
  // non-Low risk — means this inspection IS a hard FAIL, no matter
  // what the wire status or `intended_effective_status` breadcrumb
  // says. This prevents the "9 failed components but pill says PWO"
  // regression the user reported on the Building Report PDF.
  const comps = normaliseComponents(inspection);
  const gridHardFail = comps.some(
    (c) => c.status === 'fail' && (DEFECT_RISK[c.key] || 'Medium') !== 'Low',
  );
  const fcps: FailedComponentPhoto[] = inspection?.failed_component_photos || [];
  const fcpHardFail = fcps.some((fc) => {
    // Explicit hard-fail signals win regardless of risk.
    if ((fc as any).needs_replacing === true) return true;
    if ((fc as any).severity === 'critical') return true;
    // Explicit advisory / observation flags mean this fcp is NOT a
    // hard fail — skip it.
    if (fc.advisory === true) return false;
    if ((fc as any).severity === 'observation') return false;
    // Iter-226 — an optional component (spy viewer) answered "No" with no
    // evidence is "not installed", never a hard fail.
    if (isSuppressedOptionalFcp(fc)) return false;
    const risk = String((fc.risk as string) || (fc.key ? riskFor(fc.key) : 'Medium'));
    return risk !== 'Low';
  });
  const hasHardFail = gridHardFail || fcpHardFail;

  if (hasHardFail) {
    // A real hard fail exists. Return `raw` if it's already fail/review,
    // otherwise UPGRADE pass/PWO wire values to `fail` so the report
    // pill matches the components below.
    if (raw === 'fail') return 'fail';
    return 'fail';
  }

  // ── SAFE PWO PROMOTIONS (only when no hard fails were found) ────
  const intended = String(cd.intended_effective_status || '').toLowerCase();
  if (intended === 'pass_with_observations') return 'pass_with_observations';
  if (raw === 'pass_with_observations') return 'pass_with_observations';

  // Raw pass + only advisory observations → PWO.
  if (raw === 'pass' && isAdvisoryOnly(inspection)) return 'pass_with_observations';

  // Raw fail but every fcp is advisory AND no component-grid hard fail
  // → PWO. Catches legacy rows the mobile form couldn't downgrade.
  if (isAdvisoryOnly(inspection)) {
    const anyFailComp = comps.some((c) => c.status === 'fail');
    if (!anyFailComp) return 'pass_with_observations';
  }

  // Any remaining state where every failure is Low-risk → PWO.
  if (allFailuresAreLowRisk(inspection)) return 'pass_with_observations';

  return raw;
}

/** Human-readable label for a status string. */
export function statusLabel(effective: string): string {
  const k = String(effective || '').toLowerCase();
  if (k === 'pass_with_observations') return 'Satisfactory (advisory)';
  if (k === 'no_access') return 'No access';
  if (k === 'not_inspected') return 'Not inspected';
  if (k === 'pass') return 'Satisfactory';
  if (k === 'fail') return 'Remedial action required';
  if (k === 'review') return 'Further review required';
  if (k === 'draft') return 'Draft';
  if (!k) return '—';
  return k.charAt(0).toUpperCase() + k.slice(1);
}

/** Hex colour for the status pill / border. */
export function statusColor(effective: string): string {
  if (effective === 'pass') return '#10B981';
  if (effective === 'pass_with_observations') return '#0EA5E9';
  if (effective === 'fail') return '#EF4444';
  if (effective === 'review') return '#F59E0B';
  if (effective === 'no_access') return '#6B7280';
  return '#9CA3AF';
}

/** Risk-badge palette for the inspection form + PDF. */
export function riskBadgePalette(risk: RiskLevel): { bg: string; fg: string; label: string } {
  switch (risk) {
    case 'Critical':
      return { bg: '#7F1D1D', fg: '#FFFFFF', label: 'CRITICAL RISK' };
    case 'High':
      return { bg: '#FEE2E2', fg: '#991B1B', label: 'HIGH RISK' };
    case 'Medium':
      return { bg: '#FEF3C7', fg: '#92400E', label: 'MEDIUM RISK' };
    case 'Low':
    default:
      return { bg: '#E0F2FE', fg: '#075985', label: 'LOW RISK — OBSERVATION' };
  }
}

/** Convenience: risk level for a given checklist key. */
export function riskFor(key: string): RiskLevel {
  return DEFECT_RISK[key] || 'Medium';
}

/** Convenience: defect-category bucket for a given checklist key. */
export function defectCategoryFor(key: string): string {
  return DEFECT_CATEGORY[key] || 'other_defects';
}

/**
 * Build a Report Reference Number in the canonical format used by
 * the web app: `FD-{SLUG6}-{YYYY}-{MM}-{DD}`. SLUG = first 6 upper-
 * cased alphanumerics of the building name (defaults to BUILDING).
 */
export function buildReportReferenceNumber(buildingName: string | null | undefined, date: Date): string {
  const slug =
    (String(buildingName || 'BUILDING')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 6) || 'BUILDING').padEnd(0);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `FD-${slug}-${y}-${m}-${d}`;
}
